import _asyncToGenerator from "/opt/atlassian/pipelines/agent/build/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import "regenerator-runtime/runtime.js";
import { computed, defineComponent, ref, watch } from "vue";
import { SingleCategoryEvent } from "@/test/scenarios/booking";
import { attachedVueElement } from "@/utils/helpers/scanner";
import DatabaseService from "./services/database.service";
import { useI18n } from "vue-i18n";
import { ElMessageBox } from "element-plus";
import { clearUnfinishedBookings } from "./utils/helpers/booking";
import { LogService } from "./services/logging";
import BookingModule from "./store/modules/booking";
import { monitorSynchingIssues } from "./utils/vue-helpers/booking";
import * as Sentry from "@sentry/vue";
import AppModule from "./store/modules/app";
import { useIdle } from "@vueuse/core";
import { updateApp, registerUpdate, autoUpdate, canRefresh, getTotalMemoryUsed, monitorAppPerformance } from "@/utils/vue-helpers/common";
import { DAILY_AUTO_UPDATE_LIMIT } from "./constants/app";
import { loadStatusPage, loadStonly } from "./utils/vue-helpers/scripts";
import { logPendingIgnoredAndFailedBookings, resetGrowingCollecitonsFromUI, resetGrowingCollections } from "./utils/vue-helpers/db";
import { ModalStatus } from "./constants/modal";
import ModalModule from "./store/modules/modal";
import i18n from "./lang";
import { syncLogs } from "./db/poslogs";
import { cashPointClosuresSynchHandler } from "./db/cashpoint-closure";
import { loadOffers } from "@/utils/helpers/offers";
import { checkUserAccessToken } from "@/utils/helpers/auth";
import { MEMORY_USAGE_THRESHOLD } from "./utils/constants";
import { initializeComplianceLib } from "./utils/helpers/compliance";
export default defineComponent({
  name: "app",
  setup: function setup() {
    var bookingUuid = computed(function () {
      return BookingModule.selectedBookingUUID;
    }); // if the app has been idle for 1 min then we check whether we should update/refresh the app.

    var _useIdle = useIdle(1 * 60 * 1000),
        idle = _useIdle.idle;

    watch(idle, function () {
      var totalMemoryUsed = getTotalMemoryUsed();

      if (AppModule.isUpdateAvailable) {
        LogService.info("Auto-update triggered due to idle time");
        autoUpdate();
      } else if (totalMemoryUsed > MEMORY_USAGE_THRESHOLD && !AppModule.isRefreshing && canRefresh()) {
        LogService.info("Memory consumption reached ".concat(totalMemoryUsed, " MB, going to refresh the page"));
        AppModule.setIsRefreshing(true);
        window.location.reload();
      }
    }); // Set the bookingUuid as an extra tag for Syntry

    watch(bookingUuid, function () {
      Sentry.setTags({
        bookingUuid: bookingUuid.value
      });
    }, {
      deep: true
    });
    var booking = computed(function () {
      return BookingModule.selectedBooking;
    });
    var alert = ref();

    var _useI18n = useI18n({}),
        t = _useI18n.t;

    var showUpdateBanner = computed(function () {
      return AppModule.isUpdateAvailable && !AppModule.isRefreshing && AppModule.autoUpdateCount >= DAILY_AUTO_UPDATE_LIMIT;
    }); // Listen for swUpdated event and display refresh snackbar as required.

    document.addEventListener("swUpdated", registerUpdate, {
      once: true
    });

    function openMessageBox() {
      var bookings = BookingModule.bookings;
      return ElMessageBox.confirm(t("app-update.msg-contents"), t("app-update.update-the-app"), {
        confirmButtonText: t("app-update.confirm-updates"),
        cancelButtonText: t("app-update.not-now"),
        showClose: false,
        closeOnPressEscape: false,
        closeOnClickModal: false,
        dangerouslyUseHTMLString: true
      }).then( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                LogService.info("User confirmed app updates with bookings which will be cleared", {
                  bookings: bookings
                });
                clearUnfinishedBookings();
                updateApp();

              case 3:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))).catch(function () {
        LogService.info("User cancelled app updates with bookings", {
          bookings: bookings
        });
      });
    }

    function refreshWarning(event) {
      if (ModalModule.status === ModalStatus.PaymentProgress) {
        event.preventDefault();
        event.returnValue = true; // legacy support

        if (!window.electronAPI) {
          return;
        }

        LogService.info("User tried to refresh during a card payment, prompting for confirmation");
        ElMessageBox.confirm(i18n.global.t("warning.page-refresh"), i18n.global.t("common.warning"), {
          type: "warning"
        }).then( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2() {
          return regeneratorRuntime.wrap(function _callee2$(_context2) {
            while (1) {
              switch (_context2.prev = _context2.next) {
                case 0:
                  LogService.info("User confirmed refreshing during card payment");
                  ModalModule.setStatus(ModalStatus.Hidden);
                  window.location.reload();

                case 3:
                case "end":
                  return _context2.stop();
              }
            }
          }, _callee2);
        }))).catch(function () {
          LogService.info("User cancelled refreshing during card payment");
        });
      }
    }

    return {
      alert: alert,
      t: t,
      showUpdateBanner: showUpdateBanner,
      openMessageBox: openMessageBox,
      refreshWarning: refreshWarning
    };
  },
  mounted: function mounted() {
    var _this = this;

    return _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee3() {
      return regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              LogService.info("Scanner: going to attach scanner to the App vue");
              attachedVueElement();
              window.SingleCategoryEvent = SingleCategoryEvent;
              window.DBService = DatabaseService;
              window.resetGrowingCollections = resetGrowingCollections;
              window.resetGrowingCollectionsFromUI = resetGrowingCollecitonsFromUI;

              _this.triggerSynchingIssuesMonitor();

              _this.triggerAppPerformanceMonitoring();

              loadStonly();
              loadStatusPage();
              window.addEventListener("beforeunload", _this.refreshWarning);

              _this.triggerLogsSync();

              _this.triggerCashPointClosureSync();

              _this.triggerAccessTokenCheck();

              try {
                loadOffers();
                LogService.info("Offers object instantiated");
              } catch (error) {
                LogService.info("Failed to instantiate offers object", {
                  error: error
                });
              }

              _context3.prev = 15;
              _context3.next = 18;
              return initializeComplianceLib();

            case 18:
              _context3.next = 23;
              break;

            case 20:
              _context3.prev = 20;
              _context3.t0 = _context3["catch"](15);
              LogService.info("Compliance: Failed to initialize compliance lib", {
                error: _context3.t0
              });

            case 23:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3, null, [[15, 20]]);
    }))();
  },
  methods: {
    triggerSynchingIssuesMonitor: function triggerSynchingIssuesMonitor() {
      var periodMilliSeconds = 1000 * 60 * 5;
      setInterval(function () {
        monitorSynchingIssues();
        logPendingIgnoredAndFailedBookings();
      }, periodMilliSeconds);
    },
    triggerAppPerformanceMonitoring: function triggerAppPerformanceMonitoring() {
      // Each 15 minutes we monitor how is the app doing
      var periodMilliSeconds = 1000 * 60 * 10;
      setInterval(function () {
        return monitorAppPerformance();
      }, periodMilliSeconds);
    },
    triggerLogsSync: function triggerLogsSync() {
      var periodMilliSeconds = 1000 * 5;
      setInterval(function () {
        return syncLogs();
      }, periodMilliSeconds);
    },
    triggerCashPointClosureSync: function triggerCashPointClosureSync() {
      var periodMilliSeconds = 1000 * 30;
      setInterval(function () {
        return cashPointClosuresSynchHandler();
      }, periodMilliSeconds);
    },
    triggerAccessTokenCheck: function triggerAccessTokenCheck() {
      var periodMilliSeconds = 30 * 60 * 1000;
      setInterval(function () {
        return checkUserAccessToken();
      }, periodMilliSeconds);
    }
  },
  beforeUnmount: function beforeUnmount() {
    window.removeEventListener("beforeunload", this.refreshWarning);
  }
});